// src/types/AgentDescription.ts

export type AgentRole = 'assistant' | 'user' | 'system' | 'internal';

export const AvailableAgents = {
    human_proxy_auto: {
        displayName: 'passthrough',
        role: 'system' as const
    },
    team_assistant: {
        displayName: 'completion',
        role: 'system' as const
    },
    context_extraction_analyst: {
        displayName: 'contextual integration',
        role: 'internal' as const
    },
    context_expert_workspace: {
        displayName: 'contextual integration',
        role: 'internal' as const
    },
    memory_prethought_situation: {
        displayName: 'situational awareness',
        role: 'internal' as const
    },
    memory_prethought_self: {
        displayName: 'self awareness',
        role: 'internal' as const
    },
    memory_thinker: {
        displayName: 'knowledge application',
        role: 'internal' as const
    },
    theory_of_mind_expert: {
        displayName: 'associative thinking',
        role: 'internal' as const
    },
    memory_expert: {
        displayName: 'decision making',
        role: 'internal' as const
    },
    linguist_expert: {
        displayName: 'language production',
        role: 'internal' as const
    },
} as const;

export interface AgentDescription {
    key: string;
    displayName: string;
    role?: AgentRole;
}

export class AgentDescriptionImpl implements AgentDescription {
    readonly key: string;
    readonly displayName: string;
    readonly role?: AgentRole;

    private constructor(key: string, displayName: string, role?: AgentRole) {
        this.key = key;
        this.displayName = displayName;
        this.role = role;
    }

    private static findAgentInfo(key: string, defaultRole?: AgentRole): { displayName: string; role?: AgentRole } {
        const agentInfo = AvailableAgents[key as keyof typeof AvailableAgents];
        return agentInfo || { displayName: key.replaceAll('_', ' '), role: defaultRole };
    }

    static create(key: string, defaultRole?: AgentRole): AgentDescriptionImpl {
        const { displayName, role } = this.findAgentInfo(key, defaultRole);
        return new AgentDescriptionImpl(key, displayName, role);
    }

    static formatName(key: string, defaultRole?: AgentRole): string {
        const { displayName, role } = this.findAgentInfo(key, defaultRole);
        return role === 'assistant' ? `${displayName} | AI` : displayName;
    }

    get name(): string {
        return AgentDescriptionImpl.formatName(this.key, this.role);
    }
}

export function AgentDescription(key: string, defaultRole?: AgentRole): AgentDescriptionImpl {
    return AgentDescriptionImpl.create(key, defaultRole);
}